import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileUp, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { api, type ImportPreviewDto } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Label,
  LoadingState,
  Select,
} from '@/components/ui/primitives';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/overlays';
import { formatDateTime } from '@/lib/utils';

export function ImportsPage() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [preview, setPreview] = React.useState<ImportPreviewDto | null>(null);
  const fileInput = React.useRef<HTMLInputElement>(null);

  const imports = useQuery({ queryKey: ['imports'], queryFn: () => api.listImports() });
  // Watched-folder outcomes, including files skipped because their checksum had
  // already been imported. Those write no import row, so without this the
  // Imports screen showed nothing at all for a skipped delivery.
  const watchedFiles = useQuery({ queryKey: ['watched-files'], queryFn: () => api.watchedFiles(10) });
  const workerErrors = useQuery({ queryKey: ['worker-errors'], queryFn: api.workerErrors });
  const jobStatus = useQuery({ queryKey: ['job-status'], queryFn: api.jobStatus });

  React.useEffect(() => {
    if (params.get('new') === '1') {
      fileInput.current?.click();
      params.delete('new');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadImport(file),
    onSuccess: (result) => {
      setPreview(result);
      queryClient.invalidateQueries({ queryKey: ['imports'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleWatcher = useMutation({
    mutationFn: async (start: boolean) => {
      if (start) return api.startWatchedFolder();
      await api.stopWatchedFolder();
      return { started: false };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-status'] });
      toast.success('Watched folder updated');
    },
  });

  return (
    <Page
      title="Imports"
      description="CSV, JSON and JSONL. Files are parsed on the server and streamed, so a 10,000 row list is fine."
      actions={
        <>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.json,.jsonl,.ndjson"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = '';
            }}
          />
          <Button size="sm" loading={upload.isPending} onClick={() => fileInput.current?.click()}>
            <Upload className="h-3.5 w-3.5" />
            Upload a file
          </Button>
        </>
      }
    >
      <div className="space-y-5 p-5 md:p-6">
        {workerErrors.data && workerErrors.data.length > 0 && (
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-1.5 text-red-700 dark:text-red-300">
                <AlertTriangle className="h-3.5 w-3.5" />
                Worker errors
              </CardTitle>
              <Button
                variant="outline"
                size="xs"
                onClick={async () => {
                  await api.acknowledgeWorkerErrors();
                  queryClient.invalidateQueries({ queryKey: ['worker-errors'] });
                }}
              >
                Acknowledge all
              </Button>
            </CardHeader>
            <CardBody className="space-y-1">
              {workerErrors.data.map((err) => (
                <div key={err.id} className="text-xs">
                  <span className="text-muted-foreground">{formatDateTime(err.createdAt)} — </span>
                  {err.fileName && <span className="font-medium">{err.fileName}: </span>}
                  {err.message}
                </div>
              ))}
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Watched folder</CardTitle>
            <Button
              variant="outline"
              size="xs"
              loading={toggleWatcher.isPending}
              onClick={() => toggleWatcher.mutate(!jobStatus.data?.watchedFolder.running)}
            >
              {jobStatus.data?.watchedFolder.running ? 'Stop watching' : 'Start watching'}
            </Button>
          </CardHeader>
          <CardBody className="space-y-1 text-xs">
            <p className="text-muted-foreground">
              Drop files into the inbox folder and they import automatically. A file is never read until writing
              has finished, and nothing is ever overwritten.
            </p>
            {jobStatus.data?.watchedFolder.paths && (
              <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-2xs text-muted-foreground lg:grid-cols-4">
                {(['inbox', 'processing', 'processed', 'failed'] as const).map((key) => (
                  <div key={key}>
                    <Label>{key}</Label>
                    <p className="truncate">{jobStatus.data?.watchedFolder.paths[key]}</p>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <section>
          <h2 className="section-label mb-2">Import history</h2>
          {imports.isLoading ? (
            <LoadingState />
          ) : !imports.data || imports.data.rows.length === 0 ? (
            <EmptyState
              icon={FileUp}
              title="No imports yet"
              description="Upload a CSV, JSON or JSONL file to get started."
            />
          ) : (
            <Card>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th className="w-20">Kind</th>
                    <th className="w-24">Source</th>
                    <th className="w-24">Status</th>
                    <th className="w-16 text-right">Rows</th>
                    <th className="w-20 text-right">Inserted</th>
                    <th className="w-20 text-right">Updated</th>
                    <th className="w-20 text-right">Review</th>
                    <th className="w-20 text-right">Rejected</th>
                    <th className="w-36">When</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.data.rows.map((imp) => (
                    <tr key={imp.id}>
                      <td>
                        <Link to={`/imports/${imp.id}`} className="font-medium hover:text-primary hover:underline">
                          {imp.fileName}
                        </Link>
                      </td>
                      <td className="uppercase text-muted-foreground">{imp.fileKind}</td>
                      <td className="text-muted-foreground">
                        {imp.sourceKind === 'watched_folder' ? 'Folder' : 'Upload'}
                      </td>
                      <td>
                        <Badge
                          variant={
                            imp.status === 'completed'
                              ? 'success'
                              : imp.status === 'failed'
                                ? 'danger'
                                : imp.status === 'undone'
                                  ? 'warning'
                                  : 'default'
                          }
                        >
                          {imp.status}
                        </Badge>
                      </td>
                      <td className="text-right tabular-nums">{imp.totalRows.toLocaleString()}</td>
                      <td className="text-right tabular-nums text-emerald-300">{imp.insertedCount}</td>
                      <td className="text-right tabular-nums">{imp.updatedCount}</td>
                      <td className="text-right tabular-nums text-amber-300">{imp.duplicateCandidateCount}</td>
                      <td className="text-right tabular-nums text-red-700 dark:text-red-300">{imp.rejectedCount}</td>
                      <td className="text-muted-foreground">{formatDateTime(imp.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </section>

        {/*
          Watched-folder outcomes. A file whose checksum matched an earlier
          import is skipped and creates NO import row, so it would otherwise be
          invisible here — and anyone reading the newest import row would read
          the previous delivery's numbers as if they were this file's.
        */}
        {watchedFiles.data && watchedFiles.data.rows.length > 0 && (
          <section>
            <h2 className="section-label mb-2">Watched folder</h2>
            <Card>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th className="w-40">Outcome</th>
                    <th>Detail</th>
                    <th className="w-36">When</th>
                  </tr>
                </thead>
                <tbody>
                  {watchedFiles.data.rows.map((file) => (
                    <tr key={file.id}>
                      <td className="font-medium">{file.fileName}</td>
                      <td>
                        <Badge
                          variant={
                            file.outcome === 'imported'
                              ? 'success'
                              : file.outcome === 'failed'
                                ? 'danger'
                                : 'warning'
                          }
                        >
                          {file.outcome === 'skipped_duplicate' ? 'skipped (duplicate)' : file.outcome}
                        </Badge>
                      </td>
                      <td className="text-muted-foreground">
                        {file.importId ? (
                          <Link to={`/imports/${file.importId}`} className="hover:text-primary hover:underline">
                            #{file.importId} — {file.message ?? ''}
                          </Link>
                        ) : (
                          (file.message ?? '')
                        )}
                      </td>
                      <td className="text-muted-foreground">{formatDateTime(file.processedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </section>
        )}
      </div>

      <MappingDialog preview={preview} onClose={() => setPreview(null)} />
    </Page>
  );
}

function MappingDialog({ preview, onClose }: { preview: ImportPreviewDto | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [mapping, setMapping] = React.useState<Record<string, string | null>>({});
  const [force, setForce] = React.useState(false);
  const [templateName, setTemplateName] = React.useState('');
  const [saveTemplate, setSaveTemplate] = React.useState(false);

  const fields = useQuery({ queryKey: ['import-fields'], queryFn: api.importFields, enabled: !!preview });
  const templates = useQuery({ queryKey: ['mapping-templates'], queryFn: api.mappingTemplates, enabled: !!preview });

  React.useEffect(() => {
    if (preview) {
      setMapping(preview.suggestedMapping ?? {});
      setForce(false);
      setSaveTemplate(false);
      setTemplateName('');
    }
  }, [preview]);

  const commit = useMutation({
    mutationFn: () =>
      api.commitImport(preview!.importId, {
        mapping: { fields: mapping },
        forceReimport: force,
        saveTemplateName: saveTemplate && templateName.trim() ? templateName.trim() : null,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['imports'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['duplicate-counts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
      toast.success(
        `${result.inserted} inserted, ${result.updated} updated, ${result.duplicateCandidates} to review, ${result.rejected} rejected`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!preview) return null;
  const isStructured = preview.fileKind !== 'csv';

  return (
    <Dialog open={!!preview} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>Import {preview.fileName}</DialogTitle>
          <DialogDescription>
            {isStructured
              ? 'Structured files map themselves — nested categories, sources, websites and audits are read directly.'
              : 'Map each column, then confirm. Rows that cannot be read are rejected individually and never block the rest.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {preview.duplicateOfImportId && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
              This exact file was already imported as{' '}
              <Link to={`/imports/${preview.duplicateOfImportId}`} className="font-medium text-primary hover:underline">
                #{preview.duplicateOfImportId} ({preview.duplicateOfFileName})
              </Link>
              . Tick “force reimport” to process it again.
            </div>
          )}

          {!isStructured && (
            <>
              {templates.data && templates.data.length > 0 && (
                <Field label="Start from a saved mapping">
                  <Select
                    value=""
                    onChange={(e) => {
                      const template = templates.data?.find((t) => String(t.id) === e.target.value);
                      if (template) {
                        setMapping(template.mapping.fields);
                        toast.success(`Applied “${template.name}”`);
                      }
                    }}
                  >
                    <option value="">Choose a template…</option>
                    {templates.data.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
                {fields.data?.fields.map((field) => (
                  <Field key={field.field} label={field.label}>
                    <Select
                      value={mapping[field.field] ?? ''}
                      onChange={(e) =>
                        setMapping((m) => ({ ...m, [field.field]: e.target.value || null }))
                      }
                      className={field.field === 'businessName' && !mapping.businessName ? 'border-destructive' : ''}
                    >
                      <option value="">— not mapped —</option>
                      {preview.headers.map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ))}
              </div>
            </>
          )}

          <div>
            <Label>Preview — first {preview.sampleRows.length} rows</Label>
            <div className="mt-1 max-h-64 overflow-auto rounded border border-border">
              <table className="data-table">
                <thead>
                  <tr>
                    {preview.headers.map((header) => (
                      <th key={header} className="whitespace-nowrap">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sampleRows.slice(0, 25).map((row, index) => (
                    <tr key={index}>
                      {preview.headers.map((header) => (
                        <td key={header} className="max-w-[14rem] truncate">
                          {row[header] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={force} onCheckedChange={(v) => setForce(!!v)} />
              Force reimport even if this file was processed before
            </label>
            {!isStructured && (
              <>
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox checked={saveTemplate} onCheckedChange={(v) => setSaveTemplate(!!v)} />
                  Save this mapping as a reusable template
                </label>
                {saveTemplate && (
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Template name"
                    className="max-w-sm"
                  />
                )}
              </>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={commit.isPending}
            disabled={!isStructured && !mapping.businessName}
            onClick={() => commit.mutate()}
          >
            Run import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
